"use client";

import {
  Fragment,
  isValidElement,
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { YFormatType } from "../../../context/ChartContext";
import { useTiledTimeSeries, useTileWindow } from "../../../hooks/useTiledTimeSeries";
import {
  type AssembledSeries,
  type FetchTile,
  hasTileData,
  type ResolutionLadder,
} from "../../../utils/timeSeriesTiles";
import { Button } from "../../Button";
import { EmptyState } from "../../EmptyState";
import {
  type PresetRange,
  TimeControls,
  type TimeRange,
  telemetryPresetRanges,
  type WindowSize,
  type WindowSizeOption,
} from "../../TimeControls";
import { useChartOwnsItsTitle } from "../ChartHeader";
import { ChartExpandOwnedByAncestor, useChartExpand } from "../ChartHeader/useChartExpand";
import { ChartHint } from "../ChartHint";
import { TiledTimeSeriesChart, type TiledTimeSeriesChartProps } from "../TiledTimeSeriesChart";
import { createTiledCameraSyncGroup, type TiledCameraSyncGroup } from "../TiledTimeSeriesChart/cameraSyncGroup";
import { LatestDataReadout } from "./LatestDataReadout";
import { LiveControl, type TiledTimeSeriesLiveControl } from "./LiveControl";

const D = 24 * 60 * 60 * 1000;
const NOW_TICK_MS = 60_000;

const viewportAgo = (days: number, endMs: number) => ({ startMs: endMs - days * D, endMs });
const toDateRange = (v: { startMs: number; endMs: number }): TimeRange => ({
  after: new Date(v.startMs),
  before: new Date(v.endMs),
});

export interface TiledTimeSeriesSource {
  fetchTile: FetchTile;
  ladder: ResolutionLadder;
  nativeMs: number;
  resetKey: string;
  /** Revalidate visible tiles without discarding the plot. */
  refreshKey?: string | undefined;
  /** Cancel automatic refreshes while preserving manual navigation. */
  refreshEnabled?: boolean | undefined;
  tileCostSeriesCount: number;
}

export interface TiledTimeSeriesView {
  targetTierId: string;
  renderedTierId: string | null;
  viewport: { startMs: number; endMs: number };
  followingLatest: boolean;
  /** Relative Now navigation intent, independent of whether live updates are enabled. */
  atLiveEdge?: boolean | undefined;
}

/**
 * One additional plot drawn on this explorer's camera.
 *
 * ── 🔴 Why panels rather than N explorers ────────────────────────────────────
 * A stack of telemetry panels is ONE view of one node: the reader picks a
 * window once and every metric answers for that window. N explorers would mean
 * N control bars stacked down the page, each able to disagree with the others —
 * a reader could put Voltage on last week and Current on today and have no
 * indication the two plots no longer describe the same moment.
 *
 * So the camera, the controls, the resolution ladder and the reset button stay
 * singular, and a panel contributes only its own stream and its own plot. This
 * is the shape `TiledGridTelemetryView` already ships by hand in the Explore
 * drawer; `panels` is that arrangement made reachable from a declaration.
 *
 * Each panel owns a separate `useTileWindow`, exactly as the drawer does — the
 * streams have different cadences and different tile costs, and merging them
 * into one window would make the coarsest metric dictate the resolution of the
 * finest.
 */
export interface TiledTimeSeriesPanel {
  /** Stable identity. Also the React key, so it must not be positional. */
  key: string;
  /** Accessible name for this plot's SVG `<title>`, and its heading. */
  title: string;
  /** Where this panel's points come from. Its own ladder and cost. */
  source: TiledTimeSeriesSource;
  yFormatter?: YFormatType | undefined;
  yDomain?: TiledTimeSeriesChartProps["yDomain"] | undefined;
  referenceLines?: { value: number; label?: string; color?: string }[] | undefined;
  /** Plot height in px. Panels are shorter than a lone chart by default. */
  height?: number | undefined;
  /** See `ChartBottomBarProps.hideExport`. Defaults to the explorer's own setting. */
  hideExport?: boolean | undefined;
  /** See `TiledTimeSeriesChartProps.legend`. A panel's legend is its own — it draws its own series. */
  legend?: TiledTimeSeriesChartProps["legend"] | undefined;
  /** See `TiledTimeSeriesChartProps.hiddenSeriesLabels`. */
  hiddenSeriesLabels?: ReadonlySet<string> | undefined;
  /** See `TiledTimeSeriesChartProps.onLegendItemToggle`. */
  onLegendItemToggle?: ((label: string) => void) | undefined;
  /** This panel's series children, given its own assembled streams. */
  children: (args: {
    series: AssembledSeries;
    gapMs: number;
    viewport: { startMs: number; endMs: number };
  }) => ReactNode;
}

export interface TiledTimeSeriesExplorerProps {
  source: TiledTimeSeriesSource;
  /** Accessible name for the chart. Forwarded to the underlying tiled chart's SVG `<title>`. */
  title: string;
  liveControl?: TiledTimeSeriesLiveControl | undefined;
  initialRange?: { days: number } | undefined;
  viewportEnd?: Date | undefined;
  yFormatter?: YFormatType | undefined;
  yDomain?: TiledTimeSeriesChartProps["yDomain"] | undefined;
  referenceLines?: { value: number; label?: string; color?: string }[] | undefined;
  emptyState: { title: string; description: string };
  presetRanges?: PresetRange[] | undefined;
  /** @deprecated Ignored: the explorer no longer shows a read-only window-size chip. */
  tierToWindow?: Record<string, WindowSize> | undefined;
  /** @deprecated Ignored: the explorer no longer shows a read-only window-size chip. */
  windowSizeOptions?: WindowSizeOption[] | undefined;
  height?: number | undefined;
  /**
   * Fill the parent's height instead of the fixed `height`.
   *
   * For a caller that has already reserved a box — a block inside a card, a
   * dashboard tile — where the fixed 360px plot PLUS this component's controls
   * would overflow whatever was reserved. Uses the same `min-h-0 flex-1` the
   * expanded view uses, so the plot takes what is left after the controls
   * rather than adding to them.
   */
  fitParent?: boolean | undefined;
  /** How to read or drive the chart, shown in the info control beside expand. */
  hint?: ReactNode | undefined;
  dataBounds?: { startMs?: number; endMs?: number } | undefined;
  /** Reports committed camera/tier state, including while rendered tiles fall back. */
  onViewChange?: ((view: TiledTimeSeriesView) => void) | undefined;
  /** Advance unanchored views at Now. Historical navigation pauses following. */
  followLatest?: boolean | undefined;
  /** Change to explicitly resume a navigation-paused view at Now, preserving its span. */
  followLatestKey?: string | number | undefined;
  /** Metadata for the accepted plotted data and visible window, shared with expanded view.
   * Render only; publish state through onViewChange instead. Series may include overscan.
   * maxDataTimeMs is the inclusive fetch cutoff; viewport.endMs is exclusive. */
  /**
   * A built-in "latest data in view" readout, for callers that cannot pass a
   * function.
   *
   * The declarative counterpart of `renderMetadata`: same slot, same rule,
   * selected by name. A caller that can hold a function may keep using
   * `renderMetadata`; one driven from stored JSON gets the affordance too.
   * Passing both renders both, which is a caller error rather than something to
   * silently resolve here.
   */
  latestDataReadout?: import("./LatestDataReadout").LatestDataReadoutKind | undefined;
  /** Which streams `latestDataReadout` inspects, when its defaults do not fit. */
  latestDataSeriesKeys?: string[] | undefined;
  renderMetadata?:
    | ((args: {
        series: AssembledSeries;
        viewport: { startMs: number; endMs: number };
        maxDataTimeMs: number;
      }) => ReactNode)
    | undefined;
  children: (args: {
    series: AssembledSeries;
    gapMs: number;
    viewport: { startMs: number; endMs: number };
  }) => ReactNode;
  /**
   * Extra plots stacked below the primary one, on this same camera.
   *
   * Omit it and this is exactly the single-plot explorer it has always been —
   * no sync group is created and nothing about the existing path changes. See
   * {@link TiledTimeSeriesPanel}.
   */
  /** Drop the Export control while keeping the legend. See `ChartBottomBarProps.hideExport`. */
  hideExport?: boolean | undefined;
  /**
   * Caller controls over what the plot draws, on their own row above the time controls.
   *
   * 🔴 Inside the explorer, not above it in the caller's own markup: the expanded view re-renders
   * THIS component inside a dialog, so anything the caller renders as a sibling is left behind on
   * the page and the control disappears exactly when the chart gets big enough to need it.
   */
  leadingControls?: ReactNode | undefined;
  /**
   * Caller controls that belong under the plot, right-aligned in their own row.
   */
  trailingControls?: ReactNode | undefined;
  /**
   * Drop the "Window …" readout and the "no earlier data loaded" note, keeping the rest of that row —
   * resolution, partial-coverage and refresh errors, which report something a reader cannot see for
   * themselves. For a caller whose controls already state the window.
   */
  hideWindowSummary?: boolean | undefined;
  /**
   * See `TiledTimeSeriesChartProps.xMarkers`. The function form is resolved against the assembled
   * series, for markers that are a property of the data rather than of the caller's config.
   */
  xMarkers?:
    | TiledTimeSeriesChartProps["xMarkers"]
    | ((series: AssembledSeries) => TiledTimeSeriesChartProps["xMarkers"])
    | undefined;
  /** See `TiledTimeSeriesChartProps.legend`. */
  legend?: TiledTimeSeriesChartProps["legend"] | undefined;
  /** See `TiledTimeSeriesChartProps.hiddenSeriesLabels`. */
  hiddenSeriesLabels?: ReadonlySet<string> | undefined;
  /** See `TiledTimeSeriesChartProps.onLegendItemToggle`. */
  onLegendItemToggle?: ((label: string) => void) | undefined;
  panels?: TiledTimeSeriesPanel[] | undefined;
}

export function TiledTimeSeriesExplorer({
  source,
  hideExport = false,
  hideWindowSummary = false,
  leadingControls,
  trailingControls,
  xMarkers,
  legend,
  hiddenSeriesLabels,
  onLegendItemToggle,
  title,
  liveControl,
  initialRange,
  viewportEnd,
  yFormatter,
  yDomain,
  referenceLines,
  emptyState,
  presetRanges,
  height = 360,
  fitParent = false,
  hint,
  dataBounds,
  onViewChange,
  followLatest = false,
  followLatestKey,
  children,
  latestDataReadout,
  latestDataSeriesKeys,
  renderMetadata,
  panels,
}: TiledTimeSeriesExplorerProps) {
  /*
   * One shared camera for the whole stack, created only when there IS a stack.
   *
   * `cameraSync` previews an in-progress pan/zoom on sibling plots frame by
   * frame, imperatively — nothing re-renders through React until the gesture
   * settles. Without it the panels would still agree once a gesture ended
   * (they share `ts.viewport`), but during the drag the primary plot would move
   * and the others would sit still, which reads as the stack coming apart.
   *
   * `useMemo` with no panels returns undefined, so a single-plot explorer
   * creates nothing and passes nothing — the existing path is untouched.
   */
  const hasPanels = panels !== undefined && panels.length > 0;
  const cameraSync = useMemo(() => (hasPanels ? createTiledCameraSyncGroup() : undefined), [hasPanels]);
  const [width, setWidth] = useState(900);
  const refreshEnabled = source.refreshEnabled !== false;

  const staticEndMs = dataBounds?.endMs;
  const [nowTick, setNowTick] = useState(() => staticEndMs ?? Date.now());

  const anchorEndMs = viewportEnd?.getTime() ?? staticEndMs;
  const canFollow = followLatest && viewportEnd === undefined && dataBounds === undefined;
  const [followRequested, setFollowRequested] = useState(true);
  const [manualNavigationPending, setManualNavigationPending] = useState<string | null>(null);
  const followingLatest = canFollow && followRequested;
  const [loadOrigin, setLoadOrigin] = useState({ resetKey: source.resetKey, automatic: false });
  const atLiveEdge = viewportEnd === undefined && dataBounds === undefined && followRequested;
  const initialDays = initialRange?.days ?? 30;
  const initialViewport = useMemo(
    () => viewportAgo(initialDays, anchorEndMs ?? Date.now()),
    [initialDays, anchorEndMs]
  );

  const previousClockRequest = useRef({
    resetKey: source.resetKey,
    refreshKey: source.refreshKey,
    refreshEnabled,
    followLatestKey,
  });
  const refreshClockRequested =
    refreshEnabled &&
    (previousClockRequest.current.resetKey !== source.resetKey ||
      previousClockRequest.current.refreshKey !== source.refreshKey ||
      previousClockRequest.current.refreshEnabled === false);
  const sameClockSource = previousClockRequest.current.resetKey === source.resetKey;
  const resumeClockRequested = canFollow && previousClockRequest.current.followLatestKey !== followLatestKey;
  // The tile loader runs its effects before the camera does. Capture the current
  // cutoff during the refresh render so its first query includes the signal's time.
  const maxFutureMs = staticEndMs ?? (refreshClockRequested || resumeClockRequested ? Date.now() : nowTick);
  useEffect(() => {
    previousClockRequest.current = {
      resetKey: source.resetKey,
      refreshKey: source.refreshKey,
      refreshEnabled,
      followLatestKey,
    };
    if (refreshClockRequested || resumeClockRequested) {
      if (sameClockSource) setLoadOrigin({ resetKey: source.resetKey, automatic: true });
      setNowTick(maxFutureMs);
    }
  }, [
    source.resetKey,
    source.refreshKey,
    refreshEnabled,
    followLatestKey,
    refreshClockRequested,
    resumeClockRequested,
    maxFutureMs,
    sameClockSource,
  ]);

  const [home, setHome] = useState<{ startMs: number; endMs: number } | null>(null);
  const [lastSelection, setLastSelection] = useState<
    { presetId: string } | { range: { startMs: number; endMs: number } } | null
  >(null);
  const homeViewport = home ?? initialViewport;

  const ts = useTiledTimeSeries({
    fetchTile: source.fetchTile,
    ladder: source.ladder,
    nativeMs: source.nativeMs,
    initialViewport,
    widthPx: width,
    maxFutureMs,
    tileCostSeriesCount: source.tileCostSeriesCount,
    resetKey: source.resetKey,
    ...(source.refreshKey !== undefined && { refreshKey: source.refreshKey }),
    refreshEnabled: refreshEnabled && manualNavigationPending === null,
    automaticLoads:
      (followingLatest || resumeClockRequested) &&
      ((loadOrigin.resetKey === source.resetKey && loadOrigin.automatic) ||
        (sameClockSource && (refreshClockRequested || resumeClockRequested))),
  });

  const bucketMs = ts.series.targetTier.bucketMs;
  const displayEndMs = canFollow ? (Math.floor(maxFutureMs / bucketMs) + 1) * bucketMs : maxFutureMs;
  const panEndMs = anchorEndMs == null ? Math.max(displayEndMs, ts.viewport.endMs) : displayEndMs;
  const panBounds = useMemo(
    () => ({ startMs: dataBounds?.startMs ?? 0, endMs: panEndMs }),
    [dataBounds?.startMs, panEndMs]
  );

  const clampToPanBounds = (v: { startMs: number; endMs: number }) => {
    const boundEndMs = anchorEndMs == null ? Math.max(panBounds.endMs, Date.now()) : panBounds.endMs;
    const span = Math.min(v.endMs - v.startMs, boundEndMs - panBounds.startMs);
    const endMs = Math.max(Math.min(v.endMs, boundEndMs), panBounds.startMs + span);
    return { startMs: endMs - span, endMs };
  };

  const requestFollowing = (endMs: number, relative = true) => {
    setLoadOrigin({ resetKey: source.resetKey, automatic: false });
    // A throttled tab may still display its old bound when navigation settles.
    const atNow = Math.abs(panBounds.endMs - endMs) <= NOW_TICK_MS || Math.abs(Date.now() - endMs) <= NOW_TICK_MS;
    const resume = relative && atNow;
    if (refreshEnabled) setManualNavigationPending("");
    setFollowRequested(resume);
    if (staticEndMs == null) setNowTick(Date.now());
  };

  useEffect(() => {
    if (staticEndMs != null) {
      setNowTick(staticEndMs);
      return;
    }
    if (!refreshEnabled) return;
    let handle: ReturnType<typeof setTimeout>;
    const tick = (automatic = true) => {
      const now = Date.now();
      if (automatic && followingLatest) setLoadOrigin({ resetKey: source.resetKey, automatic: true });
      setNowTick(now);
      const untilBoundary = bucketMs - (now % bucketMs);
      handle = setTimeout(tick, Math.min(NOW_TICK_MS, untilBoundary));
    };
    const foreground = () => {
      if (document.visibilityState !== "visible") return;
      clearTimeout(handle);
      tick();
    };
    tick(false);
    document.addEventListener("visibilitychange", foreground);
    return () => {
      clearTimeout(handle);
      document.removeEventListener("visibilitychange", foreground);
    };
  }, [staticEndMs, bucketMs, refreshEnabled, followingLatest, source.resetKey]);

  const previousFollowLatestKey = useRef(followLatestKey);
  useEffect(() => {
    const changed = previousFollowLatestKey.current !== followLatestKey;
    previousFollowLatestKey.current = followLatestKey;
    if (!changed || !canFollow) return;
    setFollowRequested(true);
    setNowTick(Date.now());
  }, [canFollow, followLatestKey]);

  useEffect(() => {
    if (followingLatest && refreshEnabled) setNowTick(Date.now());
  }, [followingLatest, refreshEnabled, source.resetKey, source.refreshKey]);

  const jumpTo = ts.jumpTo;
  const { startMs: cameraStartMs, endMs: cameraEndMs } = ts.viewport;
  useEffect(() => {
    if (!followingLatest) return;
    const span = Math.max(cameraEndMs - cameraStartMs, bucketMs);
    if (displayEndMs === cameraEndMs && span === cameraEndMs - cameraStartMs) return;
    setLoadOrigin({ resetKey: source.resetKey, automatic: true });
    jumpTo({ startMs: displayEndMs - span, endMs: displayEndMs });
  }, [followingLatest, displayEndMs, cameraStartMs, cameraEndMs, bucketMs, jumpTo, source.resetKey]);

  const defaultPresets = useMemo<PresetRange[]>(
    () => telemetryPresetRanges(() => anchorEndMs ?? Date.now()),
    [anchorEndMs]
  );
  const presets = presetRanges ?? defaultPresets;

  // Where Reset lands: the default view of the last explicit selection. A
  // preset is re-derived at press time so live charts get a fresh window; a
  // calendar-picked range is absolute and restored as-is.
  const resetTarget = () => {
    if (lastSelection && "presetId" in lastSelection) {
      const preset = presets.find((p) => p.id === lastSelection.presetId);
      if (preset) {
        const r = preset.getRange();
        return { startMs: r.after.getTime(), endMs: r.before.getTime() };
      }
    }
    if (lastSelection && "range" in lastSelection) return lastSelection.range;
    return viewportAgo(initialDays, anchorEndMs ?? Date.now());
  };

  const { coverage, renderTier, targetTier, isFallback, isStale } = ts.series;
  const targetTierId = targetTier.id;
  const renderedTierId = renderTier?.id ?? null;
  const { startMs: viewportStartMs, endMs: viewportEndMs } = ts.viewport;
  const onViewChangeRef = useRef(onViewChange);
  useEffect(() => {
    onViewChangeRef.current = onViewChange;
  }, [onViewChange]);
  useEffect(() => {
    onViewChangeRef.current?.({
      targetTierId,
      renderedTierId,
      viewport: { startMs: viewportStartMs, endMs: viewportEndMs },
      followingLatest,
      atLiveEdge,
    });
  }, [targetTierId, renderedTierId, viewportStartMs, viewportEndMs, followingLatest, atLiveEdge]);

  // Tier selection and parent eligibility settle after the manual camera commit.
  // A stable reported view lets new manual loads survive that automatic pause.
  useEffect(() => {
    if (manualNavigationPending === null) return;
    const viewKey = `${targetTierId}:${viewportStartMs}:${viewportEndMs}`;
    setManualNavigationPending(manualNavigationPending === viewKey ? null : viewKey);
  }, [manualNavigationPending, targetTierId, viewportStartMs, viewportEndMs]);

  const gapMs = renderTier ? renderTier.bucketMs * 1.5 : 0;

  const fmt = (ms?: number) =>
    ms == null ? "—" : new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const fmtWindow = (startMs: number, endMs: number) => {
    const opts: Intl.DateTimeFormatOptions =
      endMs - startMs < 48 * 60 * 60 * 1000
        ? { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
        : { month: "short", day: "numeric", year: "numeric" };
    const f = new Intl.DateTimeFormat("en-US", opts);
    return `${f.format(new Date(startMs))} – ${f.format(new Date(endMs))}`;
  };

  const isDrilled = followingLatest
    ? ts.viewport.endMs - ts.viewport.startMs !== homeViewport.endMs - homeViewport.startMs
    : ts.viewport.startMs !== homeViewport.startMs || ts.viewport.endMs !== homeViewport.endMs;
  const windowLoading = isStale || (ts.loading && coverage.earliestMs == null);
  const coverageGapMs = (ts.viewport.endMs - ts.viewport.startMs) * 0.03;
  const partialCoverage =
    !windowLoading && coverage.earliestMs != null && coverage.earliestMs > ts.viewport.startMs + coverageGapMs;
  const hasNoTelemetry =
    !ts.loading && coverage.earliestMs == null && coverage.latestMs == null && !hasTileData(ts.series) && !isStale;

  // The explorer owns expansion rather than the chart inside it: its time controls
  // and window readout live out here, and the point of expanding is to keep them.
  const expand = useChartExpand(title);
  // Whether the chart below will print the title itself — see the primary label further down.
  const ownsItsTitle = useChartOwnsItsTitle();

  const resolvedXMarkers = typeof xMarkers === "function" ? xMarkers(ts.series) : xMarkers;
  const chartChrome =
    hint || (!expand.isExpanded && expand.action) ? (
      <>
        {hint && <ChartHint label={`About ${title}`}>{hint}</ChartHint>}
        {!expand.isExpanded && expand.action}
      </>
    ) : null;
  const seriesChildren = unwrapFragment(children({ series: ts.series, gapMs, viewport: ts.viewport }));

  const body = (
    <div className={`flex flex-col gap-3${expand.isExpanded || fitParent ? " h-full" : ""}`}>
      {leadingControls && <div className="flex flex-wrap items-center gap-3">{leadingControls}</div>}

      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-[1_1_16rem]">
          <TimeControls
            dateRange={toDateRange(ts.viewport)}
            onDateRangeChange={(r, meta) => {
              const v = clampToPanBounds({ startMs: r.after.getTime(), endMs: r.before.getTime() });
              requestFollowing(v.endMs, !!meta?.presetId);
              setLastSelection(meta?.presetId ? { presetId: meta.presetId } : { range: v });
              setHome(v);
              ts.jumpTo(v);
            }}
            showWindowSize={false}
            presetRanges={presets}
            allowDatePicker
            showReset={false}
          />
        </div>
        <div className="ml-auto flex max-w-full flex-wrap items-start gap-2">
          {liveControl && (
            <LiveControl {...liveControl} status={ts.refreshError ? "refresh-failed" : liveControl.status} />
          )}
          {chartChrome &&
            (hasNoTelemetry ? (
              <div className="invisible flex items-start gap-2" aria-hidden inert>
                {chartChrome}
              </div>
            ) : (
              chartChrome
            ))}
        </div>
      </div>

      <div className="@container" onPointerOver={titleTruncatedItems} onFocus={titleTruncatedItems}>
        <div className="flex flex-col gap-x-3 gap-y-1 overflow-hidden text-xs text-text-secondary @[512px]:flex-row @[512px]:items-center">
          <div className="flex min-w-0 items-center gap-3 whitespace-nowrap">
            {!hideWindowSummary && (
              <>
                <span className="min-w-0 truncate">
                  Window{" "}
                  <span className="font-medium text-text-primary">
                    {fmtWindow(ts.viewport.startMs, ts.viewport.endMs)}
                  </span>
                </span>
                <span className="shrink-0 text-text-tertiary">•</span>
              </>
            )}
            <span className="shrink-0">
              Resolution <span className="font-medium text-text-primary">{renderTier?.id ?? targetTier.id}</span>
              {isFallback && <span className="text-text-tertiary"> · loading {targetTier.id}</span>}
            </span>
          </div>
          <span className="flex h-4 min-w-0 items-center gap-3 overflow-hidden @[512px]:h-auto @[512px]:flex-1 @[512px]:empty:hidden [&>*]:min-w-0 [&>*]:flex-nowrap [&>*]:truncate [&>*>*]:min-w-0 [&>*>*]:truncate">
            {latestDataReadout && (
              <LatestDataReadout
                kind={latestDataReadout}
                series={ts.series}
                viewport={ts.viewport}
                maxDataTimeMs={maxFutureMs}
                {...(latestDataSeriesKeys !== undefined ? { seriesKeys: latestDataSeriesKeys } : {})}
              />
            )}
            {renderMetadata?.({ series: ts.series, viewport: ts.viewport, maxDataTimeMs: maxFutureMs })}
            {ts.loading && <span className="text-text-tertiary">loading…</span>}
            {ts.refreshError && (
              <span role="status" className="text-text-tertiary">
                {coverage.earliestMs == null ? "Unable to load telemetry" : "Refresh failed · showing previous data"}
              </span>
            )}
            {partialCoverage && (
              <span className="text-text-tertiary">showing available history from {fmt(coverage.earliestMs)}</span>
            )}
            {!hideWindowSummary && !partialCoverage && !windowLoading && ts.atDataStart && (
              <span className="text-text-tertiary">no earlier data loaded</span>
            )}
          </span>
        </div>
      </div>

      {/* In a stack the primary is just the first plot, so it carries the same label every panel
          below it does — but only where its own `ChartHeader` will not. Inside a titled section or
          card every chart suppresses its title (see `ChartLabelledBy`), which left the top plot of
          a labelled stack as the only unnamed one; outside one the header still shows it, and a
          span here would print it twice. Without panels nothing is rendered either way. */}
      {hasPanels && !ownsItsTitle && <span className="text-[11px] font-medium text-text-secondary">{title}</span>}

      <div
        className={`relative overflow-hidden rounded-md border border-border-subtle${
          expand.isExpanded || fitParent ? " min-h-0 flex-1" : ""
        }`}
        style={expand.isExpanded || fitParent ? undefined : { height }}
      >
        {/* Overlaid on the plot's top-right rather than placed in the control row: it appears and
            disappears with the zoom, and a control that comes and goes in a fixed row reflows the
            ones beside it. Here it covers empty plot corner and nothing moves. */}
        {isDrilled && !hasNoTelemetry && (
          <div className="absolute top-2 right-2 z-10">
            <Button
              variant="secondary"
              size="sm"
              onPress={() => {
                const target = clampToPanBounds(resetTarget());
                requestFollowing(target.endMs, !(lastSelection && "range" in lastSelection));
                setHome(target);
                ts.jumpTo(target);
              }}
            >
              ⟲ Reset
            </Button>
          </div>
        )}

        {hasNoTelemetry ? (
          <EmptyState title={emptyState.title} description={emptyState.description} />
        ) : (
          <>
            <TiledTimeSeriesChart
              title={title}
              hideExport={hideExport}
              viewport={ts.viewport}
              panBounds={panBounds}
              getPanBounds={() => ({
                startMs: panBounds.startMs,
                endMs: staticEndMs ?? Math.max(panBounds.endMs, Date.now()),
              })}
              onViewportChange={(viewport) => {
                requestFollowing(viewport.endMs);
                ts.onViewportChange(viewport);
              }}
              onPlotWidthChange={setWidth}
              {...(cameraSync && { cameraSync })}
              {...(yFormatter && { yFormatter })}
              {...(yDomain && { yDomain })}
              {...(referenceLines && { referenceLines })}
              {...(resolvedXMarkers?.length && { xMarkers: resolvedXMarkers })}
              {...(legend && { legend })}
              {...(hiddenSeriesLabels && { hiddenSeriesLabels })}
              {...(onLegendItemToggle && { onLegendItemToggle })}
            >
              {seriesChildren}
            </TiledTimeSeriesChart>
            {ts.loading && (
              <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 animate-pulse bg-[var(--color-viz-categorical-1)] opacity-60" />
            )}
          </>
        )}
      </div>
      {/*
        The panel stack, on the camera the controls above already drive.

        🔴 Rendered OUTSIDE the primary plot's box, not inside it: that box is
        the one `fitParent` and the expanded view stretch, and nesting the
        panels in it would make them fight the primary plot for the same
        reserved height.
      */}
      {/* 🔴 Panels do not report their plot width. That width is measured AFTER the y-axis gutter,
          which each panel sizes from its own tick labels — "130" and "-2000" are not the same
          width — so letting them all write the one width state makes them overwrite each other
          every frame and the stack never settles. The primary plot owns it; the tier it picks is
          the tier the whole stack should use anyway. */}
      {hasPanels &&
        panels.map((panel) => (
          <ExplorerPanel
            key={panel.key}
            panel={panel}
            hideExport={panel.hideExport ?? hideExport}
            viewport={ts.viewport}
            panBounds={panBounds}
            staticEndMs={staticEndMs}
            maxFutureMs={maxFutureMs}
            widthPx={width}
            onViewportChange={(viewport) => {
              requestFollowing(viewport.endMs);
              ts.onViewportChange(viewport);
            }}
            {...(cameraSync && { cameraSync })}
          />
        ))}

      {trailingControls && (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">{trailingControls}</div>
        </div>
      )}
    </div>
  );

  return <ChartExpandOwnedByAncestor>{expand.present(body)}</ChartExpandOwnedByAncestor>;
}

const truncationTitles = new WeakMap<Element, string>();

function titleTruncatedItems(event: SyntheticEvent<HTMLElement>) {
  if (!event.currentTarget.contains(event.target as Node)) return;
  for (let item = event.target as Element | null; item && item !== event.currentTarget; item = item.parentElement) {
    const truncated = item.scrollWidth > item.clientWidth;
    const ours = truncationTitles.has(item) && item.getAttribute("title") === truncationTitles.get(item);
    if (!ours) truncationTitles.delete(item);
    if (truncated && (ours || !item.hasAttribute("title"))) {
      const title = item.textContent?.replace(/\s+/g, " ").trim() ?? "";
      item.setAttribute("title", title);
      truncationTitles.set(item, title);
    } else if (!truncated && ours) {
      truncationTitles.delete(item);
      item.removeAttribute("title");
    }
  }
}

/**
 * One panel below the primary plot, fetching its own stream on the shared camera.
 *
 * A component rather than inline JSX because each panel needs its OWN
 * `useTileWindow` — hooks cannot run in a loop body, and the streams genuinely
 * differ: SCADA voltage and tap position have different cadences and different
 * per-tile costs, so one window for all of them would let the coarsest metric
 * dictate the resolution of the finest.
 *
 * It takes the camera as props and never owns it. Everything the reader can
 * drive — window, presets, reset, live — belongs to the explorer above.
 */
/**
 * A render-prop that returns `<>…</>` hands back ONE element, and the chart reads its series by
 * walking its direct children — so the fragment hides every one of them. It then has no datasets to
 * scale against, builds a degenerate y-domain, and React draws the lines inside it anyway: every
 * series flat on one row. Both the primary plot and the panels unwrap here.
 */
function unwrapFragment(rendered: ReactNode): ReactNode {
  return isValidElement(rendered) && rendered.type === Fragment
    ? (rendered.props as { children?: ReactNode }).children
    : rendered;
}

function ExplorerPanel({
  panel,
  hideExport,
  viewport,
  panBounds,
  staticEndMs,
  maxFutureMs,
  widthPx,
  onViewportChange,
  cameraSync,
}: {
  panel: TiledTimeSeriesPanel;
  hideExport: boolean;
  viewport: { startMs: number; endMs: number };
  panBounds: { startMs: number; endMs: number };
  staticEndMs: number | undefined;
  /**
   * The same inclusive fetch cutoff the primary loader uses.
   *
   * 🔴 Not derivable from the viewport, which is why it is threaded rather than
   * recomputed. The loader fetches OVERSCAN beyond the visible window, so a
   * panel without this cutoff pulls tiles past `dataBounds.endMs` — the bound
   * a host sets to say "this element has no data after here". The panel then
   * draws samples the primary plot deliberately excludes, and the two plots
   * disagree about where the data ends while sitting on one camera.
   */
  maxFutureMs: number;
  widthPx: number;
  onViewportChange: (viewport: { startMs: number; endMs: number }) => void;
  cameraSync?: TiledCameraSyncGroup;
}) {
  /*
   * `useTileWindow`, not `useTiledTimeSeries` — the difference is who owns the
   * camera. The latter holds its own viewport state, which is precisely what a
   * panel must NOT do: the explorer above owns it, and a panel with its own
   * copy would drift out of the stack the first time anyone panned.
   */
  const ts = useTileWindow({
    fetchTile: panel.source.fetchTile,
    ladder: panel.source.ladder,
    nativeMs: panel.source.nativeMs,
    viewport,
    widthPx,
    maxFutureMs,
    resetKey: panel.source.resetKey,
    tileCostSeriesCount: panel.source.tileCostSeriesCount,
    ...(panel.source.refreshKey !== undefined ? { refreshKey: panel.source.refreshKey } : {}),
    ...(panel.source.refreshEnabled !== undefined ? { refreshEnabled: panel.source.refreshEnabled } : {}),
  });
  // This panel's OWN rendered tier, not the primary plot's: a coarser stream
  // draws wider gaps, and borrowing the primary's bucket would either join
  // points across a real outage or break a continuous line into dashes.
  const gapMs = ts.series.renderTier ? ts.series.renderTier.bucketMs * 1.5 : 0;

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-text-secondary">{panel.title}</span>
      <div
        className="relative overflow-hidden rounded-md border border-border-subtle"
        style={{ height: panel.height ?? 220 }}
      >
        <TiledTimeSeriesChart
          title={panel.title}
          hideExport={hideExport}
          viewport={viewport}
          panBounds={panBounds}
          getPanBounds={() => ({
            startMs: panBounds.startMs,
            endMs: staticEndMs ?? Math.max(panBounds.endMs, Date.now()),
          })}
          onViewportChange={onViewportChange}
          {...(cameraSync && { cameraSync })}
          {...(panel.yFormatter && { yFormatter: panel.yFormatter })}
          {...(panel.yDomain && { yDomain: panel.yDomain })}
          {...(panel.referenceLines && { referenceLines: panel.referenceLines })}
          {...(panel.legend && { legend: panel.legend })}
          {...(panel.hiddenSeriesLabels && { hiddenSeriesLabels: panel.hiddenSeriesLabels })}
          {...(panel.onLegendItemToggle && { onLegendItemToggle: panel.onLegendItemToggle })}
        >
          {unwrapFragment(panel.children({ series: ts.series, gapMs, viewport }))}
        </TiledTimeSeriesChart>
        {ts.loading && (
          <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 animate-pulse bg-[var(--color-viz-categorical-1)] opacity-60" />
        )}
      </div>
    </div>
  );
}
